import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import {
    NotebookMessage,
    NotebookMessages,
} from "@notebook/domain/models/notebook-message.model";
import { NotebookMessageDeleteUseCase } from "@notebook/domain/usecases/notebook-message-delete.usecase";
import { NotebookMessageGetAllUseCase } from "@notebook/domain/usecases/notebook-message-get-all.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class DiaryComponentViewmodel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    showAddMessageModal = false;

    @observable
    showEditMessageModal = false;

    @observable
    showDetailMessageModal = false;

    @observable
    notebookMessages: NotebookMessages = NotebookMessages.empty();

    @observable
    selectedNotebookMessage: Nullable<Readonly<NotebookMessage>> = null;

    @observable
    notebookId: Undefinable<string> = undefined;

    constructor(
        @inject(NotebookMessageGetAllUseCase)
        private readonly notebookMessageGetAllUseCase: NotebookMessageGetAllUseCase,
        @inject(NotebookMessageDeleteUseCase)
        private readonly notebookMessageDeleteUseCase: NotebookMessageDeleteUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.fetchNotebookMessages()]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    toggleShowAddMessageModal(): void {
        this.showAddMessageModal = !this.showAddMessageModal;
    }

    @action
    toggleShowEditMessageModal(): void {
        this.showEditMessageModal = !this.showEditMessageModal;
    }

    @action
    toggleShowDetailMessageModal(): void {
        this.showDetailMessageModal = !this.showDetailMessageModal;
    }

    @action
    async fetchNotebookMessages(): Promise<void> {
        if (!this.notebookId) throw new Error("notebookId is not defined");

        const notebookMessages =
            await this.notebookMessageGetAllUseCase.execute(this.notebookId);

        runInAction(() => {
            this.notebookMessages = notebookMessages;
        });
    }

    @action
    async deleteNotebookMessage(notebookMessageId: string): Promise<void> {
        if (!this.notebookId) throw new Error("notebookId is not defined");

        LoadLayoutStore.start();

        const deletedMessage = await this.notebookMessageDeleteUseCase.execute(
            this.notebookId,
            notebookMessageId,
        );

        if (deletedMessage) {
            ToastManagerRepository.success();
            await this.reloadNotebookMessages();
        }

        LoadLayoutStore.finish();
    }

    @action
    setSelectedNotebookMessage(
        notebookMessage: Nullable<Readonly<NotebookMessage>>,
        readonly = false,
    ): void {
        runInAction(() => {
            this.selectedNotebookMessage = notebookMessage;
            if (readonly) {
                this.toggleShowDetailMessageModal();
            } else {
                this.toggleShowEditMessageModal();
            }
        });
    }

    @action
    setNotebookId(notebookId: string): void {
        runInAction(() => {
            this.notebookId = notebookId;
        });
    }

    async reloadNotebookMessages(): Promise<void> {
        LoadLayoutStore.start();
        await this.fetchNotebookMessages();
        LoadLayoutStore.finish();
    }
}
